import React, { useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import WalletBadge from "@/components/WalletBadge";
import ActionDock from "@/components/ActionDock";
import LineupBuilder from "@/components/LineupBuilder";
import TuttoONienteCard from "@/components/TuttoONienteCard";
import SupporterInviteBox from "@/components/SupporterInviteBox";
import OnboardingBanner from "@/components/OnboardingBanner";
import { GufoIcon } from "@/components/GufoIcon";
import { DashboardSkeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import { Camera, Clock, Award, Lock } from "lucide-react";

const PLACEHOLDERS = {
  shield: "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  jersey: "https://images.unsplash.com/photo-1552066379-e7bfd22155c5?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  coach: "https://images.unsplash.com/photo-1762505031623-1e9a2f31858e?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingCoach, setUploadingCoach] = useState(false);
  const coachInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/team");
      setData(data);
      // Cache team name/girone for the PWA splash personalized greeting.
      try {
        const tn = data?.team?.team_name;
        if (tn) {
          localStorage.setItem("fife:my_team_name", tn);
          if (data?.team?.girone) localStorage.setItem("fife:my_girone", data.team.girone);
        } else {
          localStorage.removeItem("fife:my_team_name");
          localStorage.removeItem("fife:my_girone");
        }
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCoachChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Serve un'immagine");
    setUploadingCoach(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/coach-photo", fd);
      toast.success("Foto del fantallenatore aggiornata!");
      await load();
    } catch {
      toast.error("Upload fallito");
    } finally {
      setUploadingCoach(false);
      e.target.value = "";
    }
  };

  if (loading || !data) {
    return (
      <div className="App">
        <Header />
        <DashboardSkeleton />
      </div>
    );
  }

  const pending = data.pending || data.user.status !== "approved";
  const team = data.team;

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6 pb-32 md:pb-16 w-full" style={{ paddingBottom: "calc(8rem + env(safe-area-inset-bottom, 0px))" }}>
        {/* Top: team header + wallet */}
        <div className="flex items-start justify-between flex-wrap gap-4 fade-up">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Stagione 2026/27 · Girone {data.user.girone || "-"}</div>
            <h1 className="display font-black text-4xl md:text-5xl mt-1" data-testid="team-name-heading">
              {team?.team_name || (pending ? "In attesa di approvazione" : "Squadra senza nome")}
            </h1>
            <p className="text-slate-500 mt-1">{data.user.full_name}</p>
          </div>
          <WalletBadge amount={data.user.gufi_wallet ?? 0} />
        </div>

        {pending && (
          <div className="card-surface rounded-2xl p-6 flex items-start gap-4" data-testid="pending-banner">
            <Clock className="w-6 h-6 shrink-0" style={{ color: "var(--brand)" }} />
            <div>
              <div className="display font-bold text-lg">La tua registrazione è <span style={{ color: "var(--brand)" }}>in attesa</span></div>
              <p className="text-slate-500 text-sm mt-1">
                Appena l'Admin ti approva e assegna nome squadra, stemma, maglia e rosa, vedrai qui la tua area completa.
                Nel frattempo hai già i tuoi <b className="inline-flex items-center gap-1"><GufoIcon size={14}/> 3 Gufi</b> nel wallet.
              </p>
            </div>
          </div>
        )}

        {/* Onboarding: banner "Carica stemma/maglia/foto" per approvati sprovvisti */}
        {!pending && team && <OnboardingBanner team={team} onUpdated={load} />}

        {/* Actions dock (desktop grid) */}
        <ActionDock user={data.user} onGufi={(newAmt) => setData((d) => ({ ...d, user: { ...d.user, gufi_wallet: newAmt } }))} onAction={load} />

        {/* Tutto o Niente card */}
        {!pending && team && (
          <TuttoONienteCard user={data.user} onAction={load} />
        )}

        {/* Cerca supporter — link condivisibile via WhatsApp / Email */}
        {!pending && team && <SupporterInviteBox teamName={team.team_name} coachName={data.user?.full_name} />}

        {/* Identity + Pitch + Roster */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Identity column */}
          <div className="lg:col-span-3 space-y-4">
            <IdentityCard title="Stemma" src={team?.shield_path ? fileUrl(team.shield_path) : PLACEHOLDERS.shield} testid="shield-img" />
            <IdentityCard title="Maglia" src={team?.jersey_path ? fileUrl(team.jersey_path) : PLACEHOLDERS.jersey} testid="jersey-img" />
            <div className="card-surface rounded-2xl overflow-hidden">
              <div className="aspect-square bg-slate-50 relative">
                <img
                  src={team?.coach_path ? fileUrl(team.coach_path) : PLACEHOLDERS.coach}
                  alt="Fantallenatore"
                  className="w-full h-full object-cover"
                  data-testid="coach-img"
                />
                <button
                  onClick={() => coachInput.current?.click()}
                  disabled={uploadingCoach || pending}
                  className="absolute bottom-3 right-3 glass rounded-full p-2 hover:bg-slate-100 disabled:opacity-50"
                  data-testid="coach-upload-btn"
                  title="Carica foto fantallenatore"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input ref={coachInput} type="file" accept="image/*" onChange={onCoachChange} className="hidden" data-testid="coach-file-input" />
              </div>
              <div className="p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Fantallenatore</div>
                <div className="text-sm text-slate-700">{data.user.full_name}</div>
              </div>
            </div>
          </div>

          {/* Pitch + roster */}
          <div className="lg:col-span-9">
            {pending || !team ? (
              <div className="card-surface rounded-3xl p-8 text-center text-slate-500">
                Formazione e rosa disponibili dopo l'approvazione dell'Admin.
              </div>
            ) : (
              <LineupBuilder team={team} onSaved={() => load()} />
            )}
          </div>
        </div>

        {/* Storico conferenze + indicatori Gufate */}
        <UserInsights />

        {/* Achievements / Trofei personali */}
        {!pending && <MyAchievementsCard />}
      </div>
      <Footer />
    </div>
  );
}

function MyAchievementsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/achievements")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // Rilevamento nuovi trofei: confetti + toast celebrativi
    api.post("/me/achievements/check", {}).then(({ data }) => {
      const news = data?.new_unlocked || [];
      for (const a of news) {
        const isEpic = a.tier === "legend" || a.tier === "gold";
        try {
          window.dispatchEvent(new CustomEvent("fife:confetti", {
            detail: { origin: `achievement:${a.id}`,
                      colors: isEpic ? ["#fbbf24", "#dc2626", "#f59e0b"] : undefined,
                      count: isEpic ? 240 : 140 }
          }));
        } catch {}
        toast.success(`${a.icon} Nuovo trofeo: ${a.name}!`, {
          description: `${a.description} — ${a.tier.toUpperCase()}`,
          duration: isEpic ? 8000 : 5000,
        });
      }
    }).catch(() => {});
  }, []);
  if (loading || !data) return null;
  const { unlocked = [], locked = [], stats = {}, total = 0, unlocked_count = 0 } = data;
  const tierColors = {
    legend: { bg: "linear-gradient(135deg, #fbbf24, #f59e0b, #dc2626)", ring: "#f59e0b", txt: "#78350f" },
    gold:   { bg: "linear-gradient(135deg, #fde68a, #f59e0b)",           ring: "#f59e0b", txt: "#78350f" },
    silver: { bg: "linear-gradient(135deg, #e2e8f0, #94a3b8)",           ring: "#94a3b8", txt: "#334155" },
    bronze: { bg: "linear-gradient(135deg, #fed7aa, #c2410c)",           ring: "#c2410c", txt: "#7c2d12" },
  };
  const nearlyUnlocked = locked.slice(0, 3);
  return (
    <div className="card-surface rounded-2xl overflow-hidden mt-10" data-testid="dashboard-achievements">
      <div className="p-4 md:p-5 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "linear-gradient(135deg, #fbbf24, #dc2626)" }}>
          <Award className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">La tua bacheca</div>
          <div className="display font-black text-lg">I Miei Trofei · {unlocked_count}/{total}</div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full transition-all duration-500"
                 style={{ width: `${(unlocked_count / Math.max(total, 1)) * 100}%`,
                          background: "linear-gradient(90deg, #fbbf24, #dc2626)" }} />
          </div>
        </div>
        <div className="text-xs text-slate-500 hidden md:block">
          Bet: <b>{stats.bets_placed}</b> · Vinte: <b>{stats.bets_won}</b> · Net Gufi: <b>{stats.total_net}</b>
        </div>
      </div>
      {unlocked.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm">
          <div className="text-3xl mb-2">🎯</div>
          Non hai ancora sbloccato nessun trofeo. Piazza la tua prima scommessa in <b>Fantascommesse</b> per iniziare!
        </div>
      ) : (
        <div className="p-4 md:p-5">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {unlocked.map((a) => {
              const t = tierColors[a.tier] || tierColors.bronze;
              return (
                <div key={a.id}
                     className="rounded-xl p-2.5 border-2 text-center bg-white transition-all hover:scale-105"
                     style={{ borderColor: t.ring }}
                     title={`${a.name} · ${a.description}`}
                     data-testid={`my-badge-${a.id}`}>
                  <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center text-xl"
                       style={{ background: t.bg }}>
                    {a.icon}
                  </div>
                  <div className="display font-black text-[10px] mt-1 leading-tight" style={{ color: t.txt }}>
                    {a.name}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest mt-0.5" style={{ color: t.ring }}>
                    {a.tier}
                  </div>
                </div>
              );
            })}
          </div>
          {nearlyUnlocked.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-2">
                <Lock className="w-3.5 h-3.5" /> Prossimi a sbloccarsi
              </div>
              <div className="space-y-2">
                {nearlyUnlocked.map((a) => (
                  <div key={a.id} className="flex items-center gap-3" data-testid={`nearly-${a.id}`}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg bg-slate-100 grayscale opacity-70 shrink-0">
                      {a.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-700 truncate">{a.name}</span>
                        <span className="text-slate-500">{a.progress?.current}/{a.progress?.target}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-slate-400 transition-all"
                             style={{ width: `${a.progress?.pct || 0}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 text-center">
            <a href="/fantascommesse?tab=trofei" className="text-xs font-semibold underline"
               style={{ color: "var(--brand)" }} data-testid="all-trofei-link">
              Vedi tutti i trofei →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}


const TONE_META = {
  fiducia:       { label: "FIDUCIA",       color: "#0ea5e9", emoji: "💪" },
  rabbia:        { label: "SFOGO",         color: "#ef4444", emoji: "🔥" },
  ironia:        { label: "SFOTTÒ",        color: "#f59e0b", emoji: "😏" },
  dichiarazione: { label: "ESCLUSIVA",     color: "#3b56e0", emoji: "📣" },
  disperazione:  { label: "DISPERAZIONE",  color: "#8b5cf6", emoji: "😭" },
  resa:          { label: "RESA",          color: "#64748b", emoji: "🏳️" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s fa`;
  if (s < 3600) return `${Math.floor(s / 60)}m fa`;
  if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
  return `${Math.floor(s / 86400)}g fa`;
}

function UserInsights() {
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState({ sent_top: [], received_top: [], sent_total: 0, received_total: 0 });
  useEffect(() => {
    api.get("/me/interviews").then(({ data }) => setInterviews(data || [])).catch(() => {});
    api.get("/me/gufate/stats").then(({ data }) => setStats(data || {})).catch(() => {});
  }, []);

  return (
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="user-insights">
      {/* Storico conferenze */}
      <div className="card-surface rounded-2xl p-5 lg:col-span-3" data-testid="my-interviews">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Sala stampa personale</div>
            <div className="display font-black text-lg">Le tue conferenze stampa · {interviews.length}</div>
          </div>
        </div>
        {interviews.length === 0 ? (
          <div className="text-slate-500 text-sm italic py-6 text-center">Non hai ancora convocato una conferenza stampa. Usa il tasto "Convoca conferenza stampa" per fare la tua prima uscita pubblica.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {interviews.map((it) => {
              const t = TONE_META[it.tone] || TONE_META.dichiarazione;
              return (
                <div key={it.id} className="rounded-xl border border-slate-200 p-3" data-testid={`my-interview-${it.id}`}
                     style={{ borderLeft: `4px solid ${t.color}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${t.color}22`, color: t.color }}>
                      {t.emoji} {t.label}
                    </span>
                    <span className="text-[10px] text-slate-400">{timeAgo(it.created_at)}</span>
                  </div>
                  <div className="display font-bold text-sm text-slate-900">«{it.headline}»</div>
                  <p className="text-slate-600 text-xs mt-1 italic">{it.quote}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top 3 gufati preferiti */}
      <TopBox
        title="I tuoi gufati preferiti"
        subtitle={`${stats.sent_total} gufate totali inviate`}
        emoji="🎯"
        color="#7c3aed"
        rows={stats.sent_top?.map((r) => ({ user_id: r.user_id, name: r.target_team || r.target_name, girone: r.target_girone, count: r.count })) || []}
        testid="my-gufate-sent"
      />

      {/* Top 3 chi mi gufa di più */}
      <TopBox
        title="Chi ti gufa di più"
        subtitle={`${stats.received_total} gufate totali ricevute`}
        emoji="👻"
        color="#f97316"
        rows={stats.received_top?.map((r) => ({ user_id: r.user_id, name: r.from_team || r.from_name, girone: r.from_girone, count: r.count })) || []}
        testid="my-gufate-received"
      />

      {/* Rapporto totale */}
      <div className="card-surface rounded-2xl p-5 flex flex-col justify-center" data-testid="gufate-ratio">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Bilancia gufate</div>
        <div className="display font-black text-3xl mt-1 flex items-baseline gap-2">
          <span style={{ color: "#7c3aed" }}>{stats.sent_total || 0}</span>
          <span className="text-slate-300 text-sm">inviate</span>
          <span className="text-slate-300 mx-1">·</span>
          <span style={{ color: "#f97316" }}>{stats.received_total || 0}</span>
          <span className="text-slate-300 text-sm">ricevute</span>
        </div>
        <div className="text-slate-500 text-xs mt-2">
          {stats.sent_total > stats.received_total ? "Sei più temuto che gufato: hai il pallino in mano." :
           stats.sent_total < stats.received_total ? "Ti gufano più di quanto tu gufi: ricambia con classe." :
           "Perfetto equilibrio karmico da sala stampa."}
        </div>
      </div>
    </div>
  );
}

function TopBox({ title, subtitle, emoji, color, rows, testid }) {
  return (
    <div className="card-surface rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg" style={{ background: `${color}18` }}>{emoji}</div>
        <div>
          <div className="display font-black text-base">{title}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-slate-400 text-xs italic py-4 text-center">Ancora nessun dato.</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.user_id || i} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2" data-testid={`${testid}-row-${i}`}>
              <span className="display font-black text-lg tabular-nums w-6" style={{ color }}>#{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800 truncate">{r.name || "—"}</div>
                {r.girone && <div className="text-[10px] uppercase tracking-widest text-slate-500">Girone {r.girone}</div>}
              </div>
              <span className="display font-black text-lg tabular-nums" style={{ color }}>{r.count}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function IdentityCard({ title, src, testid }) {
  return (
    <div className="card-surface rounded-2xl overflow-hidden">
      <div className="aspect-square bg-slate-50">
        <img src={src} alt={title} className="w-full h-full object-cover" data-testid={testid} />
      </div>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
      </div>
    </div>
  );
}
